import './ResultDetails.scss';

import { useMemo } from 'react';
import { Nav, Tab } from 'react-bootstrap';

import { Sanitize } from '@components/Sanitize';
import { OrderDetailResult, OrderResultFinding, ReportType } from '@customTypes/order';
import { OrderType } from '@pages/results/Results.types';
import { getVariantClassificationLabel } from '@pages/results/Results.utils';

import { ApoeSummaryPane, hasApoeSummary } from './ApoeSummaryPane';
import NextStepsPane from './NextStepsPane';
import ResultPane from './ResultPane';
import { hasRiskScoreSummary, RiskScoreSummaryPane } from './RiskScoreSummaryPane';
import SingleGeneSummaryPane from './SingleGeneSummaryPane';

interface IProps {
  orderType?: OrderType;
  results: OrderDetailResult;
  productName: string;
  reportType?: ReportType;
}

enum ResultTab {
  RESULT = 'result',
  SUMMARY = 'summary',
  NEXT = 'next',
}

export const ResultDetails = ({ orderType = OrderType.PGX, results, productName, reportType }: IProps) => {
  const showResultTab = useMemo(() => orderType === OrderType.SINGLE_GENE, [orderType]);
  const isSingleGenePositive = useMemo(
    () => orderType === OrderType.SINGLE_GENE && results?.toplines?.[0]?.finding !== OrderResultFinding.NEGATIVE,
    [orderType, results],
  );
  const riskScoreHasSummary = useMemo(
    () => orderType === OrderType.RISK_SCORE && hasRiskScoreSummary(results.clinical_risk_inputs, results.toplines),
    [orderType, results.clinical_risk_inputs, results.toplines],
  );
  const apoeHasSummary = useMemo(
    () => orderType === OrderType.APOE && hasApoeSummary(results.toplines),
    [orderType, results.toplines],
  );
  const showSummaryTab = riskScoreHasSummary || isSingleGenePositive || apoeHasSummary;
  const defaultActiveKey = useMemo(() => {
    if (showSummaryTab) {
      return ResultTab.SUMMARY;
    }
    if (showResultTab) {
      return ResultTab.RESULT;
    }
    return ResultTab.NEXT;
  }, [showResultTab, showSummaryTab]);
  // FAMILY_VARIANT_TESTING reports omit top-level clinical_summary / disease_information,
  // so fall back to the per-classification interpretation and gene_info.
  const clinicalSummary =
    results.clinical_summary ||
    (results.classifications ?? [])
      .map(classification => classification.interpretation_description)
      .filter((value): value is string => !!value)
      .join('\n\n');
  const diseaseInformation =
    results.disease_information ??
    (results.classifications ?? [])
      .map(classification => classification.gene_info)
      .filter((value): value is string => !!value);
  const variantCalls = (results.classifications ?? [])
    .map(classification => getVariantClassificationLabel(classification.classification))
    .filter(Boolean);
  return (
    <Tab.Container id='result-detail-tabs' defaultActiveKey={defaultActiveKey}>
      <div className='result-details'>
        <Nav variant='pills'>
          <Nav.Item hidden={!showSummaryTab}>
            <Nav.Link eventKey={ResultTab.SUMMARY}>Summary</Nav.Link>
          </Nav.Item>
          <Nav.Item hidden={!showResultTab}>
            <Nav.Link eventKey={ResultTab.RESULT}>Result</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={ResultTab.NEXT}>Next Steps/Resources</Nav.Link>
          </Nav.Item>
        </Nav>

        <Sanitize as='div' className='result-details-content'>
          <Tab.Content>
            <Tab.Pane eventKey={ResultTab.RESULT} hidden={!showResultTab} mountOnEnter>
              <ResultPane
                classifications={results.classifications}
                clinicalSummary={clinicalSummary}
                isSingleGenePositive={isSingleGenePositive}
              />
            </Tab.Pane>
            <Tab.Pane eventKey={ResultTab.SUMMARY} mountOnEnter>
              {orderType === OrderType.APOE ? (
                <ApoeSummaryPane summary={results.toplines} />
              ) : isSingleGenePositive ? (
                <SingleGeneSummaryPane
                  summary={clinicalSummary}
                  diseaseInformation={diseaseInformation}
                  variantCalls={variantCalls}
                />
              ) : (
                <RiskScoreSummaryPane patientInfo={results.clinical_risk_inputs} summary={results.toplines} />
              )}
            </Tab.Pane>
            <Tab.Pane eventKey={ResultTab.NEXT} mountOnEnter>
              <NextStepsPane
                nextSteps={results.next_steps}
                orderType={orderType}
                productName={productName}
                reportType={reportType}
              />
            </Tab.Pane>
          </Tab.Content>
        </Sanitize>
      </div>
    </Tab.Container>
  );
};
